import {
  describeAgentsDestination,
  type AgentsExitContext,
  type AgentsSurface,
} from '@lib/analytics/agentsExit';
import type { ModalTab } from '@lib/media/modalTab';
import { ReactionManager } from '@lib/mobx/reaction-manager';
import type { ScheduleTimeZoneSource, SocialConnectSource } from '@lib/social/scheduling/types';
// `@lib/verbs/ids`, not `@lib/verbs`: the barrel pulls in the glyph catalog, which imports
// `NAV_ICONS` from `@pages` — and this store is constructed at app boot.
import { legacyMenuActionId, type LibraryVerbId, type MediaVerbId } from '@lib/verbs/ids';
import { userStore } from '@stores/User';
import mixpanel from 'mixpanel-browser';
import { action, makeObservable, observable } from 'mobx';
import { debounce } from 'throttle-debounce';

/**
 * Which control changed a library's pin. Declared here rather than in `LibraryFavorites` because
 * it's telemetry vocabulary, and this way the store that fires the event needs no new import.
 */
export type LibraryPinControl = 'card' | 'table' | 'mobile-drawer';

/** The surfaces a first-plan checkout can start from; carried through to its activation. */
export type CheckoutSource = 'paywall' | 'pricing-page';

const MIXPANEL_TOKEN = process.env.MIXPANEL_TOKEN;
const IS_DEV = process.env.NODE_ENV === 'development';
const EVENT_LOG_KEY = 'debug.trackingEventLog';
const TRACKING_ENABLED_KEY = 'debug.trackingEnabled';
const EVENT_LOG_MAX = 200;

export type TrackedEvent = {
  event: string;
  properties?: Record<string, unknown>;
  timestamp: number;
};

export class TrackingStore extends ReactionManager {
  private _isInitialized = false;
  private _crossStoreReactionsBound = false;

  // Whether Mixpanel event dispatch is enabled.
  // Off by default in dev (toggle on via Debug tab), always on in prod.
  @observable
  trackingEnabled: boolean;

  @observable
  eventLogEnabled: boolean;

  @observable
  eventLog: TrackedEvent[] = [];

  constructor() {
    super();

    this.eventLogEnabled = localStorage.getItem(EVENT_LOG_KEY) === 'true';

    // In dev, default to off unless explicitly enabled via debug toggle.
    // In prod, default to on unless explicitly disabled.
    const storedPref = localStorage.getItem(TRACKING_ENABLED_KEY);
    this.trackingEnabled = storedPref !== null ? storedPref === 'true' : !IS_DEV;

    makeObservable(this);

    if (!MIXPANEL_TOKEN) {
      console.warn('[Tracking] No MIXPANEL_TOKEN found, analytics disabled.');
      return;
    }

    mixpanel.init(MIXPANEL_TOKEN, {
      track_pageview: false,
      // Cookie persistence scoped to the parent domain (.starzero.ai), so the
      // anonymous id minted on the marketing landing carries into this app and
      // merges with the logged-in identity — all surfaces report to the shared
      // "StarZero Unified" Mixpanel project.
      persistence: 'cookie',
      cross_subdomain_cookie: true,
      // Respect browser Do-Not-Track setting. Users with DNT enabled won't be
      // tracked, which may undercount analytics — this is intentional to honor
      // user privacy preferences.
      ignore_dnt: false,
      debug: IS_DEV,
      autocapture: !IS_DEV,
      record_sessions_percent: IS_DEV ? 0 : 100,
    });

    // Super property distinguishing this surface from the other apps
    // (landing, agents) reporting into the unified project.
    mixpanel.register({ app: 'app' });

    this._isInitialized = true;
  }

  /**
   * Bind the reaction that reads `userStore` — which sits in an import cycle
   * with this module. Called from src/index.tsx once the whole module graph
   * has evaluated; registering it during construction (even deferred to a
   * microtask) raced module initialization in production chunk orders and
   * threw "Cannot access 'X' before initialization".
   */
  initCrossStoreReactions() {
    if (!this._isInitialized || this._crossStoreReactionsBound) return;
    this._crossStoreReactionsBound = true;
    this.reaction(
      () => userStore.data,
      (userData) => {
        if (userData) {
          this._identify(userData);
        } else {
          this._reset();
        }
      },
      { fireImmediately: true },
    );
  }

  @action
  setTrackingEnabled(enabled: boolean) {
    this.trackingEnabled = enabled;
    localStorage.setItem(TRACKING_ENABLED_KEY, String(enabled));
  }

  @action
  setEventLogEnabled(enabled: boolean) {
    this.eventLogEnabled = enabled;
    if (enabled) {
      localStorage.setItem(EVENT_LOG_KEY, 'true');
    } else {
      localStorage.removeItem(EVENT_LOG_KEY);
      this.eventLog = [];
    }
  }

  @action
  clearEventLog() {
    this.eventLog = [];
  }

  private _identify(user: {
    _id: string;
    email?: string;
    name?: { first?: string; last?: string; nickname?: string };
    createdAt?: number;
  }) {
    if (!this._isInitialized || !this.trackingEnabled || !user?._id) return;

    try {
      mixpanel.identify(user._id);
      const createdAt =
        user.createdAt && Number.isFinite(user.createdAt)
          ? new Date(user.createdAt).toISOString()
          : undefined;
      mixpanel.people.set({
        $email: user.email ?? undefined,
        $name: user.name?.nickname ?? user.name?.first ?? user.email ?? undefined,
        $created: createdAt,
      });
    } catch (e) {
      console.warn('[Tracking] identify failed:', e);
    }
  }

  private _reset() {
    if (!this._isInitialized) return;
    try {
      mixpanel.reset();
    } catch (e) {
      console.warn('[Tracking] reset failed:', e);
    }
  }

  @action
  private _track(event: string, properties?: Record<string, unknown>) {
    if (this.eventLogEnabled) {
      console.log(`%c[Track] ${event}`, 'color: #6ecf7a; font-weight: bold', properties ?? '');
      this.eventLog.push({ event, properties, timestamp: Date.now() });
      if (this.eventLog.length > EVENT_LOG_MAX) {
        this.eventLog = this.eventLog.slice(-EVENT_LOG_MAX);
      }
    }

    if (!this._isInitialized || !this.trackingEnabled) return;
    try {
      mixpanel.track(event, properties);
    } catch (e) {
      console.warn('[Tracking] track failed:', event, e);
    }
  }

  // ──────────────────────────────────────────────
  //  Page Views
  // ──────────────────────────────────────────────

  pageView(path: string) {
    this._track('Page View', { path });
  }

  // ──────────────────────────────────────────────
  //  Auth Events
  // ──────────────────────────────────────────────

  login(method: 'email' | 'sso' | 'magic-link' = 'email') {
    this._track('Login', { method });
  }

  loginFailed(error: string) {
    this._track('Login Failed', { error });
  }

  signUp(method: 'email' | 'sso' = 'email') {
    this._track('Sign Up', { method });
  }

  /**
   * Fired when the user explicitly picks a login method (magic link vs password)
   * — via the toggle on the login screen or by registering with a password.
   * `context` distinguishes the surface so we can see method preference for
   * returning logins vs. new sign-ups. Drives adoption of the remembered-method
   * default. See `LoginPreferencesStore`.
   */
  loginMethodSelected(method: 'magic-link' | 'password', context: 'login' | 'register') {
    this._track('Login Method Selected', { method, context });
  }

  signUpFailed(error: string) {
    this._track('Sign Up Failed', { error });
  }

  logout() {
    this._track('Logout');
  }

  emailVerified() {
    this._track('Email Verified');
  }

  passwordResetRequested() {
    this._track('Password Reset Requested');
  }

  passwordResetCompleted() {
    this._track('Password Reset Completed');
  }

  // `hadPassword: false` = a magic-link user setting their first password.
  passwordChanged(params: { hadPassword: boolean }) {
    this._track('Password Changed', params);
  }

  magicLinkRequested(params: { redirect: boolean }) {
    this._track('Magic Link Requested', params);
  }

  magicLinkRequestFailed(error: string) {
    this._track('Magic Link Request Failed', { error });
  }

  // ──────────────────────────────────────────────
  //  Navigation
  // ──────────────────────────────────────────────

  navItemClicked(label: string, destination: string, extra?: Record<string, unknown>) {
    this._track('Nav Item Clicked', { label, destination, ...extra });
  }

  // ──────────────────────────────────────────────
  //  StarZero Agents app handoffs
  // ──────────────────────────────────────────────

  /**
   * The user is leaving for the StarZero Agents app. Every outbound navigation
   * fires this — one event with a `destination` property rather than one event
   * name per page, so a single dashboard answers "where did this session go?".
   *
   * Exists because these handoffs are cross-origin, so Mixpanel otherwise reads
   * them as idle time or a session exit with no indication of where the user
   * went. `destination` / `destinationLabel` / `targetHost` are derived from the
   * URL, so call sites only supply the surface, the control, and IDs.
   *
   * Carries **no user-authored strings** — no library/media/chat/podcast names.
   * Those are PII (media names are raw upload filenames), and the IDs answer the
   * question on their own. See `AgentsExitContext`.
   *
   * `surface: 'auth'` marks the two redirect handoffs in `PostAuthRedirect.ts`
   * (the `?redirect=` login bounce and the post-onboarding cookie destination),
   * which are not deliberate clicks — filter on it when counting intentional
   * exits.
   *
   * Fired *before* the short-lived token exchange in `openAuthedChatUrl`, so a
   * handoff the user initiated is recorded even when the exchange fails and no
   * navigation happens. This event therefore means "tried to go to the agents
   * app", not "arrived" — the right signal for the idle-time question, since a
   * user who bounced off a token error also looks idle.
   *
   * (Ordering is not a flush concern: mixpanel-browser batches by default, so
   * `track` just queues to localStorage and delivery on unload is handled by
   * mixpanel's own visibilitychange/pagehide → sendBeacon handler.)
   *
   * `requestedNewTab` is the *intent*, not the outcome: the event is emitted
   * before `window.open` is attempted, and a blocked popup (the user-gesture
   * window can expire during the token exchange, notably on iOS Safari) falls
   * back to same-tab navigation. Named for what it actually records.
   */
  agentsAppOpened(
    params: {
      /** The fully-built outbound URL; the destination is derived from it. */
      url: string;
      /** Whether a new tab was *asked for*. See the note above. */
      requestedNewTab: boolean;
    } & AgentsExitContext,
  ) {
    const { url, ...rest } = params;
    const { destination, destinationLabel, isAgentsApp, targetHost } =
      describeAgentsDestination(url);

    this._track('Agents App Opened', {
      ...rest,
      destination,
      destinationLabel,
      isAgentsApp,
      targetHost,
    });
  }

  // Fired when the user opens the changelog from the dot on the sidebar logo.
  // `version` says which build they were on; the changelog itself is fetched at
  // runtime from the shared feed (see `stores/Changelog.ts`), so it is not tied
  // to the build.
  //
  // NOT one event per user per version: the dot persists in a grey read state
  // once there's nothing new (see `ChangelogDot.tsx`), so the same user can
  // reopen the same version any number of times. `unread` is what separates a
  // first read from a revisit — count first reads with `unread = true`, or the
  // per-version numbers double-count anyone who looks twice. It's also how to
  // tell whether the quiet read-state door actually gets used.
  changelogViewed({ unread }: { unread: boolean }) {
    this._track('Changelog Viewed', { version: process.env.VERSION, unread });
  }

  // Which action a workspace user takes from the signpost bento. `signpost` is the
  // semantic CARD id (not an index) so renames don't break the funnel dashboard;
  // `action` / `mode` say which button within the card was clicked. We also report
  // the card's `title`, its `position` in the rendered list (1-based), and the
  // layout `variant` — position shifts between the first-time and returning layouts
  // (and any future variants), so it's only meaningful alongside the variant.
  // `itemIndex` (1-based) identifies which row was clicked on cards that list
  // user-specific entries (favorite libraries, podcast clips).
  workspaceSignpostClicked(
    signpost:
      | 'podcast'
      | 'add_media'
      | 'work_with_library'
      | 'find_moments'
      | 'favorites'
      | 'your_clips',
    props?: {
      mode?: 'visual' | 'transcript' | 'unified';
      libraryId?: string;
      action?: string;
      title?: string;
      position?: number;
      variant?: 'first_time' | 'returning';
      itemIndex?: number;
    },
  ) {
    this._track('Workspace Signpost Clicked', { signpost, ...props });
  }

  // Fired when the user retargets a workspace signpost (search / AI-agent) to a
  // different library via the inline picker. `source` is the card the picker sits on.
  workspaceLibraryPicked(params: { libraryId: string; source: 'work_with_library' }) {
    this._track('Workspace Library Picked', params);
  }

  // Fired when the user changes their display name. `source` distinguishes the
  // surface (e.g. the workspace greeting's inline editor vs Settings).
  nicknameChanged(source: string) {
    this._track('Nickname Changed', { source });
  }

  // ──────────────────────────────────────────────
  //  Search Events
  // ──────────────────────────────────────────────

  searchPerformed(params: {
    query?: string;
    hasImage: boolean;
    hasClip: boolean;
    resultType: string;
    libraryCount: number;
  }) {
    this._track('Search Performed', params);
  }

  /**
   * A search settled with no moments to show, and why.
   *
   * `reason` is the semantic id from `zeroResultReason` — 'processing',
   * 'empty-scope', 'unreachable', 'nothing-usable', 'no-scope', 'no-match' — so
   * renaming a heading never breaks a dashboard.
   *
   * The distribution is the point. Visual search applies no score threshold, so a
   * scope with indexed media always returns something; 'no-match' should therefore
   * be rare, and a lot of it would mean either that assumption is wrong or that
   * transcript-only searches are failing more than we think. 'processing' being
   * common would say people search before their uploads are ready, and
   * 'unreachable' being common would say stale library scopes are a real problem
   * rather than an edge case.
   */
  searchReturnedNothing(params: { reason: string; resultType: string; libraryCount: number }) {
    this._track('Search Returned Nothing', params);
  }

  searchResultClicked(params: {
    mediaId: string;
    libraryId: string;
    resultIndex: number;
    resultType: string;
  }) {
    this._track('Search Result Clicked', params);
  }

  searchImageAdded(method: 'paste' | 'drag-drop' | 'upload') {
    this._track('Search Image Added', { method });
  }

  searchImageButtonClicked() {
    this._track('Search Image Button Clicked');
  }

  searchImageRemoved() {
    this._track('Search Image Removed');
  }

  searchClipUsed(params: { clipId: string; resetText: boolean }) {
    this._track('Search Clip Used', params);
  }

  // ──────────────────────────────────────────────
  //  Library Selector Events
  // ──────────────────────────────────────────────

  librarySelectorOpened(params: { libraryCount: number; selectedCount: number }) {
    this._track('Library Selector Opened', params);
  }

  librarySelectorClosed(params: {
    selectedCount: number;
    selectionChanged: boolean;
    isMultiSelect: boolean;
  }) {
    this._track('Library Selector Closed', params);
  }

  libraryMultiSelectToggled(params: {
    enabled: boolean;
    /**
     * Which picker's switch this was. The search bar's drives the persisted search scope;
     * a `destination` picker's is local to that popover and commits nothing until confirmed
     * — so they answer different questions and must not land in one undifferentiated count.
     * Absent on the search bar's, which shipped before this field existed.
     */
    picker?: 'destination';
  }) {
    this._track('Library Multi Select Toggled', params);
  }

  librarySelectAllClicked(params: { libraryCount: number; filtered: boolean }) {
    this._track('Library Select All Clicked', params);
  }

  libraryDeselectAllClicked(params: { libraryCount: number; filtered: boolean }) {
    this._track('Library Deselect All Clicked', params);
  }

  // Debounced: rapid typing in library filter fires one event
  private _debouncedLibraryFilterSearched = debounce(500, (query: string) => {
    this._track('Library Filter Searched', { query });
  });

  libraryFilterSearched(params: { query: string }) {
    this._debouncedLibraryFilterSearched(params.query);
  }

  // Debounced: rapid library checkbox toggles fire one aggregate event
  private _debouncedSearchLibraryChanged = debounce(
    300,
    (libraryIds: string[], libraryCount: number) => {
      this._track('Search Library Changed', { libraryIds, libraryCount });
    },
  );

  searchLibraryChanged(params: { libraryIds: string[]; libraryCount: number }) {
    this._debouncedSearchLibraryChanged(params.libraryIds, params.libraryCount);
  }

  searchResultTypeChanged(resultType: string) {
    this._track('Search Result Type Changed', { resultType });
  }

  // ──────────────────────────────────────────────
  //  Search Result Actions
  // ──────────────────────────────────────────────

  searchSimilarClicked(params: { mediaId: string; libraryId: string }) {
    this._track('Search Similar Clicked', params);
  }

  searchAskAnAgentClicked(params: {
    mediaId: string;
    libraryId: string;
    /**
     * Which control sent the clip. Several surfaces fire this and they are not
     * interchangeable: the card overlay can only hand over a result's original bounds, while
     * the moment preview sends whatever range the user dragged the timeline to — so folding
     * them into one count would hide the only thing the preview surface adds.
     *
     * Inside the preview it splits three ways, because the preview's own redesign is what has
     * to be measured: the accented CTA, the ⋯ menu row and the mobile drawer row all reach the
     * same handler. They shared `'moment-modal-rail'` for one commit, which made "does burying
     * the verbs cost their usage" unanswerable on the one surface where the CTA is loudest.
     *
     * `'moment-modal-rail'` is HISTORICAL — that rail is gone. Kept in the union because the
     * value is in the dashboards.
     *
     * Absent on the card's, which shipped before this field existed. Keeping it optional
     * rather than backfilling the card is deliberate: this event already has charts built on
     * it, and adding a required property would shift their totals at deploy for anyone who
     * doesn't add a filter.
     */
    // The same vocabulary `mediaMenuAction` uses — it is the same question ("which control?")
    // about the same set of buttons — plus the historical rail. Spelled out rather than
    // imported, like the union on `mediaMenuAction`, to keep stores free of page imports.
    control?:
      | 'moment-modal-rail'
      | 'media-kebab'
      | 'media-drawer'
      | 'media-card-rail'
      | 'media-modal-cta'
      | 'media-modal-secondary'
      | 'media-table-rail';
  }) {
    this._track('Search Ask An Agent Clicked', params);
  }

  // Debounced: rapid checkbox clicks fire one aggregate event
  private _debouncedMomentSelectionChanged = debounce(300, (totalSelected: number) => {
    this._track('Moment Selection Changed', { totalSelected });
  });

  momentSelectionChanged(params: { totalSelected: number }) {
    this._debouncedMomentSelectionChanged(params.totalSelected);
  }

  // ──────────────────────────────────────────────
  //  Moment Modal Events
  // ──────────────────────────────────────────────

  momentModalOpened(params: {
    variant: 'search-result' | 'library-media-card';
    mediaId: string;
    libraryId: string;
  }) {
    this._track('Moment Modal Opened', params);
  }

  momentModalPanelChanged(params: { panel: ModalTab; mediaId: string; libraryId: string }) {
    this._track('Moment Modal Panel Changed', params);
  }

  momentDownloadCompleted(params: {
    format: 'mp4' | 'gif' | 'both';
    removeWatermark: boolean;
    mediaId: string;
    libraryId: string;
    durationSeconds: number;
  }) {
    this._track('Moment Download Completed', params);
  }

  momentDownloadFailed(params: { mediaId: string; libraryId: string; error?: string }) {
    this._track('Moment Download Failed', params);
  }

  transcriptDownloaded(mediaId: string) {
    this._track('Transcript Downloaded', { mediaId });
  }

  transcriptSentenceClicked(params: { timestamp: number }) {
    this._track('Transcript Sentence Clicked', params);
  }

  timelineSeeked(params: { timestamp: number }) {
    this._track('Timeline Seeked', params);
  }

  momentFormatChanged(params: { format: 'mp4' | 'gif' | 'both' }) {
    this._track('Moment Format Changed', params);
  }

  momentWatermarkToggled(params: { removeWatermark: 'yes' | 'no' }) {
    this._track('Moment Watermark Toggled', params);
  }

  private _debouncedTimelineZoomChanged = debounce(500, (zoomLevel: number) => {
    this._track('Timeline Zoom Changed', { zoomLevel });
  });

  timelineZoomChanged(params: { zoomLevel: number }) {
    this._debouncedTimelineZoomChanged(params.zoomLevel);
  }

  // ──────────────────────────────────────────────
  //  Video Player Events
  // ──────────────────────────────────────────────

  videoModeChanged(mode: 'moment' | 'full') {
    this._track('Video Mode Changed', { mode });
  }

  // ──────────────────────────────────────────────
  //  Pricing / Billing Events
  // ──────────────────────────────────────────────

  pricingPageViewed() {
    this._track('Pricing Page Viewed');
  }

  checkoutStarted(params: {
    productId: string;
    productType: 'subscription' | 'one-time';
    /** Which surface started it; absent for the pre-existing in-app callers. */
    source?: CheckoutSource;
  }) {
    this._track('Checkout Started', params);
  }

  /**
   * Fired each time the paywall is shown in an actionable state (not on the confirming return
   * from checkout). A walled user can leave for Settings and come back, so one load can log
   * several views; dedupe to unique users in Mixpanel for the funnel. `variant` says which
   * wall: `plans` for an account with nothing attached, `portal` for a lapsed subscription
   * that has to be repaired in Stripe.
   */
  paywallViewed(params: { variant: 'plans' | 'portal' }) {
    this._track('Paywall Viewed', params);
  }

  /**
   * The end of the paywall funnel: a first-plan checkout came back and `/billing/info`
   * reports the account entitled. `productId` and `source` are null when the return landed
   * without the checkout record (the return page alone started the check).
   */
  subscriptionActivated(params: { productId: string | null; source: CheckoutSource | null }) {
    this._track('Subscription Activated', params);
  }

  subscriptionChanged(params: {
    action: 'upgrade' | 'downgrade';
    newPlanId: string;
    newPlanName: string;
  }) {
    this._track('Subscription Changed', params);
  }

  creditsPurchased() {
    this._track('Credits Purchased');
  }

  billingPortalOpened(params?: { source: string }) {
    this._track('Billing Portal Opened', params);
  }

  checkoutCancelled(params: {
    productId: string;
    productType: 'subscription' | 'one-time';
    action: 'upgrade' | 'downgrade';
  }) {
    this._track('Checkout Cancelled', params);
  }

  cancelSubscriptionClicked() {
    this._track('Cancel Subscription Clicked');
  }

  manageInvoicesClicked() {
    this._track('Manage Invoices Clicked');
  }

  invoiceDownloaded(params: { invoiceId: string }) {
    this._track('Invoice Downloaded', params);
  }

  /** A Usage card popout on Settings → Billing: `spend` is the breakdown, `notes` the credit notes. */
  creditsBreakdownOpened(params: { panel: 'spend' | 'notes' }) {
    this._track('Credits Breakdown Opened', params);
  }

  // ──────────────────────────────────────────────
  //  Library Events
  // ──────────────────────────────────────────────

  libraryViewed(params: { libraryId: string; libraryName?: string; viewMode?: string }) {
    this._track('Library Viewed', params);
  }

  libraryViewModeChanged(params: { mode: 'list' | 'grid' }) {
    this._track('Library View Mode Changed', params);
  }

  /** The Libraries page's own grid/list switch — distinct from the media view above. */
  librariesViewModeChanged(params: { mode: 'list' | 'grid' }) {
    this._track('Libraries View Mode Changed', params);
  }

  libraryCreated(params: { libraryId: string; libraryName: string }) {
    this._track('Library Created', params);
  }

  libraryRenamed(params: { libraryId: string; newName: string }) {
    this._track('Library Renamed', params);
  }

  libraryDeleted(params: { libraryId: string }) {
    this._track('Library Deleted', params);
  }

  // ──────────────────────────────────────────────
  //  Media Upload Events (enriched)
  // ──────────────────────────────────────────────

  uploadStarted(params: {
    libraryId: string;
    fileCount: number;
    totalSizeBytes: number;
    source: 'file-picker' | 'drag-drop' | 'url-paste';
  }) {
    this._track('Upload Started', params);
  }

  uploadBatchCompleted(params: {
    libraryId: string;
    successCount: number;
    /** Total non-successes (all the *FailedCount / cancelledCount below sum to this). */
    failedCount: number;
    totalAttempted: number;
    durationMs: number;
    // Failure breakdown — "bad wifi" vs "out of credits" vs "user cancelled" are
    // very different product signals; keep them as distinct counters.
    cancelledCount?: number;
    billingFailedCount?: number;
    deletedCount?: number;
    connectivityFailedCount?: number;
    otherFailedCount?: number;
    // Resilience ROI — how many files needed (and survived via) a retry, an
    // offline-park, or a finish false-negative recovery. Measures whether the
    // upload hardening actually saved uploads.
    retriedFileCount?: number;
    offlineParkedFileCount?: number;
    finishRecoveredCount?: number;
    /** Files that were mid-transfer when a stall episode began (see `uploadStalled`).
     *  Pairs with the failure breakdown to answer "do stalled files fail more?". */
    stalledFileCount?: number;
  }) {
    this._track('Upload Batch Completed', params);
  }

  /**
   * The session-wide upload stall the library header reports — no mid-transfer
   * file has moved a byte for `HEADLINE_STALL_AFTER_MS`. Fires once per episode,
   * not once per tick.
   *
   * Why we want it: the chunk layer rides out a quiet link for 45–90s before
   * aborting the part and re-PUTting it, and we picked those numbers off a
   * reasoned guess about mobile radio handovers, not measurements. This plus
   * `Upload Stall Ended` gives the stall-duration distribution that says whether
   * the leash is the right length.
   */
  uploadStalled(params: {
    inFlightFileCount: number;
    /** The fuse that elapsed, so a later retune stays comparable across cohorts. */
    thresholdMs: number;
    online: boolean;
    connectionEffectiveType?: string;
  }) {
    this._track('Upload Stalled', params);
  }

  /**
   * Closes an `Upload Stalled` episode. `resolution` matters because both cases
   * look identical in the stall flag but mean opposite things: `recovered` = the
   * link moved bytes again, `upload-ended` = nothing was left in flight (the
   * upload finished, failed, or was cancelled while still stalled).
   *
   * Caveat worth knowing when reading the funnel: the flag is population-wide, so
   * if one file dies while a sibling keeps moving this reports `recovered`. Per-file
   * outcomes live in `Upload Batch Completed`'s failure breakdown, not here.
   */
  uploadStallEnded(params: {
    stalledMs: number;
    resolution: 'recovered' | 'upload-ended';
    inFlightFileCount: number;
  }) {
    this._track('Upload Stall Ended', params);
  }

  urlPasted(params: { libraryId: string; urlCount: number }) {
    this._track('URL Pasted', params);
  }

  // ──────────────────────────────────────────────
  //  Upload Activity Panel — Rich Event Cards
  //  (drag-drop rejections, all-duplicates, processing-error)
  // ──────────────────────────────────────────────

  /** Fires when a rich-event card is added to the upload activity panel. */
  uploadRichEventShown(params: {
    kind: 'file-rejected' | 'duplicate' | 'processing-error';
    rowCount: number;
  }) {
    this._track('Upload Rich Event Shown', params);
  }

  /** Fires when the user opens the modal listing the per-file rows. */
  uploadRichEventFilesViewed(params: {
    kind: 'file-rejected' | 'duplicate' | 'processing-error';
    rowCount: number;
  }) {
    this._track('Upload Rich Event Files Viewed', params);
  }

  /** Fires when the user dismisses the card via the X button. */
  uploadRichEventDismissed(params: {
    kind: 'file-rejected' | 'duplicate' | 'processing-error';
    rowCount: number;
  }) {
    this._track('Upload Rich Event Dismissed', params);
  }

  /** Fires when the user opens the "duplicates skipped" modal on a
   * real upload BatchCard (different surface from the rich-event
   * all-duplicates card above). */
  uploadDuplicatesSkippedViewed(params: { libraryId: string; duplicatesCount: number }) {
    this._track('Upload Duplicates Skipped Viewed', params);
  }

  /** Fires when the user clicks "View in library" on a duplicate row,
   * regardless of which modal it surfaced from. `source` keeps the
   * surfaces distinguishable in the dashboard. */
  viewInLibraryClicked(params: {
    source: 'rich-event' | 'batch-card-duplicates' | 'all-results-duplicates';
    libraryId: string;
    mediaId: string;
  }) {
    this._track('View In Library Clicked', params);
  }

  // ──────────────────────────────────────────────
  //  Media Events
  // ──────────────────────────────────────────────

  mediaViewed(params: { mediaId: string; libraryId: string }) {
    this._track('Media Viewed', params);
  }

  mediaRenamed(params: { mediaId: string; libraryId: string }) {
    this._track('Media Renamed', params);
  }

  mediaDeleted(params: { mediaId: string; libraryId: string }) {
    this._track('Media Deleted', params);
  }

  // ──────────────────────────────────────────────
  //  Media Menu Actions (3-dot menu)
  // ──────────────────────────────────────────────

  mediaMenuOpened(params: { mediaId: string; libraryId: string }) {
    this._track('Media Menu Opened', params);
  }

  mediaMenuClosed(params: { mediaId: string; libraryId: string }) {
    this._track('Media Menu Closed', params);
  }

  /**
   * `action` takes the CODE id (`work-with-agent`); the agent verb goes out as
   * `ask-an-agent`, which is what this event has always emitted and what the dashboards
   * filter on. `legacyMenuActionId` does the translation — see `@lib/verbs/ids`.
   */
  mediaMenuAction(params: {
    action:
      | MediaVerbId
      | 'copy-link'
      // Debug-mode only, so this one arrives at a trickle and from a known handful of accounts.
      | 'copy-metadata'
      | 'copy-link-at-time'
      | 'open-original'
      | 'rename'
      | 'move'
      | 'download'
      | 'retry-processing'
      | 'delete';
    mediaId: string;
    libraryId: string;
    /**
     * Which control ran it. The three verbs are reachable from the desktop kebab, the mobile
     * action drawer, the grid card's title-row rail, the media modal's CTA and the pill beside
     * it, and the table row's title-cell rail, so the event name alone can't say where the click
     * came from.
     *
     * Keep in step with `MediaActionControl` in `@lib/media/mediaActions` — spelled out here
     * rather than imported, to keep stores from depending on pages.
     *
     * `'media-modal-rail'` is HISTORICAL and no longer emitted: the moment modal's three-glyph
     * rail became a single accented CTA, which reports as `'media-modal-cta'`. Kept in the union
     * because the value is still in the dashboards, and a chart filtering on it should keep
     * type-checking against this list rather than looking like a typo.
     */
    control?:
      | 'media-kebab'
      | 'media-drawer'
      | 'media-card-rail'
      | 'media-modal-cta'
      | 'media-modal-secondary'
      | 'media-modal-rail'
      | 'media-table-rail';
  }) {
    this._track('Media Menu Action', { ...params, action: legacyMenuActionId(params.action) });
  }

  // ──────────────────────────────────────────────
  //  Library Media Selection Bar Events
  // ──────────────────────────────────────────────

  // Debounced: rapid checkbox clicks fire one aggregate event
  private _debouncedMediaSelectionChanged = debounce(300, (totalSelected: number) => {
    this._track('Media Selection Changed', { totalSelected });
  });

  mediaSelectionChanged(params: { totalSelected: number }) {
    this._debouncedMediaSelectionChanged(params.totalSelected);
  }

  mediaSelectionBarSelectAll(params: { totalAvailable: number }) {
    this._track('Media Selection Bar Select All', params);
  }

  mediaSelectionBarDeselectAll() {
    this._track('Media Selection Bar Deselect All');
  }

  mediaSelectionBarAskAnAgent(params: { mediaCount: number; libraryId: string }) {
    this._track('Media Selection Bar Ask An Agent', params);
  }

  mediaSelectionBarDelete(params: { mediaCount: number; libraryId: string }) {
    this._track('Media Selection Bar Delete', params);
  }

  mediaSelectionBarRetry(params: { mediaCount: number; libraryId: string }) {
    this._track('Media Selection Bar Retry', params);
  }

  mediaSelectionBarClosed() {
    this._track('Media Selection Bar Closed');
  }

  // ──────────────────────────────────────────────
  //  Settings Events
  // ──────────────────────────────────────────────

  settingsTabViewed(tab: string) {
    this._track('Settings Tab Viewed', { tab });
  }

  // ──────────────────────────────────────────────
  //  General UI Events
  // ──────────────────────────────────────────────

  contactSalesClicked(params?: { source: string }) {
    this._track('Contact Sales Clicked', params);
  }

  dragDropInteraction(params: { type: 'file' | 'image'; target: string; fileCount: number }) {
    this._track('Drag Drop Interaction', params);
  }

  toastShown(params: { templateId: string; variant: string }) {
    this._track('Toast Shown', params);
  }

  toastDismissed(params: { toastType?: string }) {
    this._track('Toast Dismissed', params);
  }

  soundToggled(params: { enabled: boolean }) {
    this._track('Sound Toggled', params);
  }

  expandSearchClicked() {
    this._track('Expand Search Clicked');
  }

  collapseSearchClicked() {
    this._track('Collapse Search Clicked');
  }

  userMenuOpened() {
    this._track('User Menu Opened');
  }

  userMenuClosed() {
    this._track('User Menu Closed');
  }

  userMenuItemClicked(params: { item: string; destination?: string }) {
    this._track('User Menu Item Clicked', params);
  }

  libraryMenuOpened(params: { libraryId: string }) {
    this._track('Library Menu Opened', params);
  }

  libraryMenuClosed(params: { libraryId: string }) {
    this._track('Library Menu Closed', params);
  }

  /**
   * Fires for every library kebab / context-drawer action. The housekeeping members must stay
   * in step with `LibraryMenuAction` in `@pages/Libraries/components/libraryMenuActions` —
   * spelled out here rather than imported, to keep stores from depending on pages. The verb
   * members no longer have to be kept in step by hand: `VerbId` comes from `@lib/verbs/ids`,
   * which imports nothing, so it's safe for a store.
   *
   * `action` takes the CODE id (`work-with-agent`); the agent verb goes out as
   * `ask-an-agent`, which is what this event has always emitted.
   */
  libraryMenuAction(params: {
    action: LibraryVerbId | 'rename' | 'delete' | 'pin';
    libraryId: string;
  }) {
    this._track('Library Menu Action', { ...params, action: legacyMenuActionId(params.action) });
  }

  /**
   * One event for both library-scoped verbs ("Chat with an Agent", "Find
   * moments") from every surface that offers them — the rails on library cards
   * and rows, the labelled action row inside a library, and the kebab/drawer
   * menus. `surface` says which page, `control` which specific control.
   *
   * Why one event rather than two: the question this answers is "which verb do
   * people reach for, and from where?", and that is one dashboard tile only if
   * both verbs report under one name.
   *
   * Why it fires for the agent verb too, which already emits `Agents App
   * Opened`: that event exists to label a cross-origin *exit*, and "Find
   * moments" is in-app so it can never appear there. Deliberately accepting two
   * events on an agent click keeps the two verbs comparable — the same trade
   * `Agents App Opened` already makes alongside `Library Menu Action`.
   *
   * IDs only, no library name — same rule as `AgentsExitContext`.
   */
  libraryVerbClicked(params: {
    verb: LibraryVerbId;
    /** Omitted when the verb ran over several libraries — see `libraryCount`. */
    libraryId?: string;
    surface: AgentsSurface;
    control: string;
    /**
     * How many libraries the verb ran over. Only the moments verb can exceed 1 (search
     * spans libraries; Agents takes a single one), so this doubles as the signal for
     * "someone used the multi-library picker".
     */
    libraryCount?: number;
  }) {
    this._track('Library Verb Clicked', params);
  }

  /**
   * A library verb was pressed and its picker opened — the denominator for
   * `Library Verb Clicked`, which only fires once a library has been chosen.
   *
   * Without this pair, an unused verb surface and a surface people open and abandon look
   * identical, and "was this control discovered at all?" is the whole question for a new CTA.
   *
   * Deliberately its own event rather than a flag on `Library Selector Opened`: that one counts
   * the search bar's picker, and firing it for destination pickers too would shift its numbers
   * at deploy for every chart that doesn't add a filter.
   */
  libraryVerbPickerOpened(params: {
    verb: LibraryVerbId;
    surface: AgentsSurface;
    control: string;
    /** How many libraries were on offer — an empty-ish account explains a low pick rate. */
    libraryCount: number;
  }) {
    this._track('Library Verb Picker Opened', params);
  }

  /**
   * Someone clicked a library row the picker had ruled out (today: an empty library). Measures
   * how often users aim at a library they can't use from a picker.
   *
   * Carries the id, not the reason: the reason is display copy, and copy tweaks would split the
   * metric — the mistake `DESTINATION_LABELS` already made once. There is exactly one disabling
   * rule today, so the reason is implicit; add a stable code here if a second one appears.
   */
  libraryPickerRowBlocked(params: { libraryId: string }) {
    this._track('Library Picker Row Blocked', params);
  }

  libraryDeleteModalOpened(params: { libraryId: string }) {
    this._track('Library Delete Modal Opened', params);
  }

  libraryDeleteCancelled(params: { libraryId: string }) {
    this._track('Library Delete Cancelled', params);
  }

  momentMergeButtonClicked(params: { momentCount: number }) {
    this._track('Moment Merge Button Clicked', params);
  }

  manageBillingClicked(params: { source: string }) {
    this._track('Manage Billing Clicked', params);
  }

  momentModalShowInLibrary(params: { mediaId: string; libraryId: string }) {
    this._track('Moment Modal Show In Library', params);
  }

  momentModalClosed(params: { mediaId: string; context: 'search' | 'library' }) {
    this._track('Moment Modal Closed', params);
  }

  momentModalCancelled(params: { mediaId: string }) {
    this._track('Moment Modal Cancelled', params);
  }

  momentDownloadStarted(params: { mediaId: string; format: string; removeWatermark: boolean }) {
    this._track('Moment Download Started', params);
  }

  chooseMediaEditUrlsClicked(params: { errorCount: number }) {
    this._track('Choose Media Edit URLs Clicked', params);
  }

  chooseMediaSummaryConfirmed() {
    this._track('Choose Media Summary Confirmed');
  }

  chooseMediaSummaryCancelled() {
    this._track('Choose Media Summary Cancelled');
  }

  // Which destination the user picks in the Add media modal: an existing library
  // or a brand-new one (created inline). Semantic `kind`, not an index.
  uploadDestinationSelected(params: { kind: 'existing' | 'new'; libraryId?: string }) {
    this._track('Upload Destination Selected', params);
  }

  fileSelectModalOpened(params: { totalFiles: number; totalSizeBytes: number }) {
    this._track('File Select Modal Opened', params);
  }

  fileSelectModalClosed(params: { selectedCount: number; totalFiles: number }) {
    this._track('File Select Modal Closed', params);
  }

  fileSelectionChanged(params: { selectedCount: number; totalFiles: number }) {
    this._track('File Selection Changed', params);
  }

  chooseFileUploadConfirmed(params: {
    selectedCount: number;
    totalFiles: number;
    totalDurationSeconds: number;
    totalSizeBytes: number;
    creditsCost: number;
    creditsAvailable: number;
  }) {
    this._track('Choose File Upload Confirmed', params);
  }

  chooseFileUploadCancelled(params: { selectedCount: number; totalFiles: number }) {
    this._track('Choose File Upload Cancelled', params);
  }

  fileUploadSummaryConfirmed(params: {
    fileCount: number;
    totalDurationSeconds: number;
    totalSizeBytes: number;
    creditsCost: number;
    creditsAvailable: number;
  }) {
    this._track('File Upload Summary Confirmed', params);
  }

  fileUploadSummaryCancelled(params: { fileCount: number }) {
    this._track('File Upload Summary Cancelled', params);
  }

  // ──────────────────────────────────────────────
  //  Moment Selection Bar Actions
  // ──────────────────────────────────────────────

  selectionBarDownload(params: { momentCount: number }) {
    this._track('Selection Bar Download', params);
  }

  selectionBarAskAnAgent(params: { momentCount: number; libraryId: string }) {
    this._track('Selection Bar Ask An Agent', params);
  }

  selectionBarSelectAll(params: { totalAvailable: number }) {
    this._track('Selection Bar Select All', params);
  }

  selectionBarDeselectAll() {
    this._track('Selection Bar Deselect All');
  }

  selectionBarClosed() {
    this._track('Selection Bar Closed');
  }

  // ──────────────────────────────────────────────
  //  Moment Merge / Trim Events
  // ──────────────────────────────────────────────

  momentMergeModalOpened(params: { clipCount: number }) {
    this._track('Moment Merge Modal Opened', params);
  }

  momentMergeModalCancelled(params: { clipCount: number }) {
    this._track('Moment Merge Modal Cancelled', params);
  }

  momentMergeDownloaded(params: {
    clipCount: number;
    format: string;
    removeWatermark: boolean;
    totalDurationSeconds: number;
  }) {
    this._track('Moment Merge Downloaded', params);
  }

  momentTrimOpened(params: { momentId: string }) {
    this._track('Moment Trim Opened', params);
  }

  momentTrimConfirmed(params: { momentId: string }) {
    this._track('Moment Trim Confirmed', params);
  }

  momentTrimCancelled(params: { momentId: string }) {
    this._track('Moment Trim Cancelled', params);
  }

  // Debounced: rapid drag-and-drop reorders fire one aggregate event
  private _reorderCount = 0;

  private _debouncedMomentReordered = debounce(500, () => {
    this._track('Moment Reordered', { reorderCount: this._reorderCount });
    this._reorderCount = 0;
  });

  momentReordered() {
    this._reorderCount++;
    this._debouncedMomentReordered();
  }

  // ──────────────────────────────────────────────
  //  External URL Upload Events
  // ──────────────────────────────────────────────

  externalUrlUploadStarted(params: { libraryId: string; urlCount: number }) {
    this._track('External URL Upload Started', params);
  }

  externalUrlUploadCompleted(params: {
    libraryId: string;
    successCount: number;
    failedCount: number;
    durationMs: number;
  }) {
    this._track('External URL Upload Completed', params);
  }

  // Adding media from a Google Drive folder link. `fileCount` is the number of
  // supported files imported; `skippedCount` is unsupported/over-limit files
  // dropped. `source` distinguishes the multi-file picker from the single/direct
  // upload path.
  googleDriveFolderImported(params: {
    libraryId: string;
    fileCount: number;
    skippedCount: number;
    source: 'picker' | 'direct';
  }) {
    this._track('Google Drive Folder Imported', params);
  }

  // ──────────────────────────────────────────────
  //  Retry Events
  // ──────────────────────────────────────────────

  mediaRetried(params: { mediaId: string; libraryId: string }) {
    this._track('Media Retried', params);
  }

  mediaRetryAllClicked(params: { libraryId: string; failedCount: number }) {
    this._track('Media Retry All Clicked', params);
  }

  // ──────────────────────────────────────────────
  //  Bulk Delete Events
  // ──────────────────────────────────────────────

  mediaBulkDeleted(params: { mediaIds: string[]; libraryId: string; count: number }) {
    this._track('Media Bulk Deleted', params);
  }

  // ──────────────────────────────────────────────
  //  Library Folders
  // ──────────────────────────────────────────────

  /** `depth` is 1 for a folder at the library's root. */
  folderCreated(params: { libraryId: string; depth: number }) {
    this._track('Folder Created', params);
  }

  folderRenamed(params: { libraryId: string }) {
    this._track('Folder Renamed', params);
  }

  folderMoved(params: { libraryId: string; via: 'menu' | 'drag' | 'undo' }) {
    this._track('Folder Moved', params);
  }

  /**
   * `mediaCount` is how many media the backend deleted with the folder; `keptMediaCount` how many
   * the user chose to keep, moved to the folder's parent first.
   */
  folderDeleted(params: { libraryId: string; mediaCount: number; keptMediaCount: number }) {
    this._track('Folder Deleted', params);
  }

  mediaMovedToFolder(params: {
    libraryId: string;
    count: number;
    via: 'menu' | 'bulk' | 'drag' | 'undo';
  }) {
    this._track('Media Moved To Folder', params);
  }

  // ──────────────────────────────────────────────
  //  Misc Events
  // ──────────────────────────────────────────────

  forgotPasswordClicked() {
    this._track('Forgot Password Clicked');
  }

  resendVerificationEmail() {
    this._track('Resend Verification Email');
  }

  newLibraryButtonClicked() {
    this._track('New Library Button Clicked');
  }

  autoplayToggled(params: { type: 'videos' | 'moments'; enabled: boolean }) {
    this._track('Autoplay Toggled', params);
  }

  libraryFilterApplied(params: { query: string | null; context: 'libraries' | 'media' }) {
    this._track('Library Filter Applied', params);
  }

  /**
   * Fired when the Filters popover is opened, with how the user got there. `Library Filter Applied`
   * only fires once the user types, so without this an open-then-abandon is invisible — and there
   * would be no way to tell whether anyone uses the keyboard shortcut.
   */
  libraryFilterOpened(params: { context: 'libraries' | 'media'; via: 'keyboard' | 'click' }) {
    this._track('Library Filter Opened', params);
  }

  libraryFilterMatchModeChanged(params: { matchAll: boolean; context: 'libraries' | 'media' }) {
    this._track('Library Filter Match Mode Changed', params);
  }

  librarySortChanged(params: { sortBy: string }) {
    this._track('Library Sort Changed', params);
  }

  /**
   * The "Pinned first" modifier in either sort picker. Sits beside `librarySortChanged`
   * because it's the same control: it changes the order without changing the sort.
   *
   * `enabled` is the value being moved *to*, so the off direction is measurable — the point of
   * having it at all is to learn whether people keep pinned-first grouping or turn it off. No
   * surface property, matching `librarySortChanged`: desktop-vs-mobile is already answerable
   * from Mixpanel's own device properties.
   */
  libraryPinnedFirstToggled(params: { enabled: boolean }) {
    this._track('Library Pinned First Toggled', params);
  }

  /**
   * A library was pinned or unpinned. Fired from `libraryFavorites.toggleFavorite`, i.e. the one
   * place the pin list changes, so a new pin surface can't be added without supplying a `control`.
   *
   * `pinned` is the value being moved *to*, so pinning and unpinning are separable — "how many
   * people pin at all" and "how many change their mind" are different questions. `control` answers
   * the one that decides whether an affordance is working: the card's pin only appears on hover,
   * so if it goes unused next to the table's always-visible column, that's the finding.
   */
  libraryPinToggled(params: { pinned: boolean; control: LibraryPinControl; libraryId: string }) {
    this._track('Library Pin Toggled', params);
  }

  mediaDeleteModalOpened(params: {
    libraryId: string;
    mediaIds: string[];
    count: number;
    totalDurationSeconds: number;
  }) {
    this._track('Media Delete Modal Opened', params);
  }

  mediaDeleteCancelled(params: { libraryId: string; mediaIds: string[]; count: number }) {
    this._track('Media Delete Cancelled', params);
  }

  creditsHoverOpened(params: { creditsLeft: number }) {
    this._track('Credits Hover Opened', params);
  }

  creditsHoverClosed() {
    this._track('Credits Hover Closed');
  }

  upgradeClicked(params: { source: string }) {
    this._track('Upgrade Clicked', params);
  }

  chooseMediaModalOpened(params: { totalVideos: number; libraryId: string }) {
    this._track('Choose Media Modal Opened', params);
  }

  chooseMediaModalCancelled(params: { libraryId: string }) {
    this._track('Choose Media Modal Cancelled', params);
  }

  chooseMediaImported(params: {
    selectedCount: number;
    totalAvailable: number;
    filteredOutCount: number;
    duplicateCount: number;
    totalDurationSeconds: number;
    creditsCost: number;
    creditsAvailable: number;
  }) {
    this._track('Choose Media Imported', params);
  }

  addVideosButtonClicked(params: { libraryId: string }) {
    this._track('Add Videos Button Clicked', params);
  }

  // Debounced: rapid paste/type actions that increase URL count fire one event
  private _debouncedUploadModalLinkPasted = debounce(
    300,
    (params: { libraryId: string; urlCount: number; urls: string[] }) => {
      // Cap URLs to avoid oversized Mixpanel properties; urlCount is always the true total
      const capped = params.urls.length > 10 ? params.urls.slice(0, 10) : params.urls;
      this._track('Upload Modal Link Pasted', { ...params, urls: capped });
    },
  );

  uploadModalLinkPasted(params: { libraryId: string; urlCount: number; urls: string[] }) {
    this._debouncedUploadModalLinkPasted(params);
  }

  // Batch-debounced: when multiple URLs resolve near-simultaneously, flush one aggregate event
  private _pendingUrlValidations: Array<{
    url: string;
    valid: boolean;
    errorReason?: string;
    videoCount?: number;
    totalDurationSeconds?: number;
  }> = [];

  private _debouncedFlushUrlValidations = debounce(500, () => {
    const batch = this._pendingUrlValidations;
    this._pendingUrlValidations = [];
    if (batch.length === 0) return;
    this._track('Upload Modal URLs Validated', {
      urlCount: batch.length,
      validCount: batch.filter((v) => v.valid).length,
      errorCount: batch.filter((v) => !v.valid).length,
      totalVideoCount: batch.reduce((sum, v) => sum + (v.videoCount ?? 0), 0),
      totalDurationSeconds: batch.reduce((sum, v) => sum + (v.totalDurationSeconds ?? 0), 0),
    });
  });

  uploadModalUrlValidated(params: {
    url: string;
    libraryId: string;
    valid: boolean;
    errorReason?: string;
    videoCount?: number;
    totalDurationSeconds?: number;
    creditsCost?: number;
    creditsAvailable?: number;
  }) {
    this._pendingUrlValidations.push(params);
    this._debouncedFlushUrlValidations();
  }

  uploadModalFilesDropped(params: {
    libraryId: string;
    fileCount: number;
    totalSizeBytes: number;
  }) {
    this._track('Upload Modal Files Dropped', params);
  }

  uploadModalFilesSelected(params: {
    libraryId: string;
    fileCount: number;
    totalSizeBytes: number;
  }) {
    this._track('Upload Modal Files Selected', params);
  }

  addMediaClicked(params: {
    libraryId: string;
    videoCount: number;
    totalDurationSeconds: number;
    creditsCost: number;
    creditsAvailable: number;
    buttonLabel: string;
  }) {
    this._track('Add Media Clicked', params);
  }

  // ──────────────────────────────────────────────
  //  API Keys Events
  // ──────────────────────────────────────────────

  apiKeyCreateDialogOpened() {
    this._track('API Key Create Dialog Opened');
  }

  apiKeyCreateDialogCancelled() {
    this._track('API Key Create Dialog Cancelled');
  }

  apiKeyCreated(params: { name: string; scopeCount: number }) {
    this._track('API Key Created', params);
  }

  apiKeyCopied(params: { context: 'create' | 'rotate' | 'key-id' }) {
    this._track('API Key Copied', params);
  }

  apiKeyRotated(params: { keyId: string }) {
    this._track('API Key Rotated', params);
  }

  apiKeyDeleted(params: { keyId: string }) {
    this._track('API Key Deleted', params);
  }

  // ──────────────────────────────────────────────
  //  Social Publishing Events
  // ──────────────────────────────────────────────

  /** The user pressed Connect or Reconnect; the browser leaves for the platform right after. `source` is the page it started from. */
  socialConnectStarted(params: { platform: string; source: SocialConnectSource }) {
    this._track('Social Connect Started', params);
  }

  /** The platform sent the user back with a new or refreshed connection. */
  socialConnectCompleted(params: { platform: string }) {
    this._track('Social Connect Completed', params);
  }

  /** The platform sent the user back without a connection; `errorCode` is the API's, `denied` included. */
  socialConnectFailed(params: { platform: string; errorCode: string }) {
    this._track('Social Connect Failed', params);
  }

  socialAccountDisconnected(params: { platform: string }) {
    this._track('Social Account Disconnected', params);
  }

  /** The Schedule page's calendar/list toggle. */
  scheduleViewChanged(params: { view: 'calendar' | 'list' }) {
    this._track('Schedule View Changed', params);
  }

  /** The calendar's month navigation. */
  scheduleMonthChanged(params: { direction: 'prev' | 'next' | 'today' }) {
    this._track('Schedule Month Changed', params);
  }

  /** A schedule call succeeded: one post per target. `drafts` counts the targets kept unpublished (an inbox or a Page draft). */
  schedulePostCreated(params: {
    targets: number;
    drafts: number;
    /** Sorted, one entry per platform posted to. */
    platforms: string[];
    when: 'now' | 'later';
  }) {
    this._track('Schedule Post Created', params);
  }

  /** An edit saved. `changed` names which part of the post moved. */
  schedulePostEdited(params: { status: string; changed: 'time' | 'content' | 'both' }) {
    this._track('Schedule Post Edited', params);
  }

  /** Cancel or retry pressed on a post's page and accepted by the API. */
  schedulePostAction(params: { action: 'cancel' | 'retry'; status: string }) {
    this._track('Schedule Post Action', params);
  }

  /** The schedule's time zone picked, from the compose hint or the schedule header. `followsDevice` is the reset. */
  scheduleTimeZoneChanged(params: {
    timeZone: string;
    followsDevice: boolean;
    source: ScheduleTimeZoneSource;
  }) {
    this._track('Schedule Time Zone Changed', params);
  }

  /** The render's media played on a compose page, once per visit. `layout` says whether it sat beside the form or folded above it. */
  scheduleMediaPreviewed(params: { page: 'create' | 'edit'; layout: 'columns' | 'stacked' }) {
    this._track('Schedule Media Previewed', params);
  }

  // ──────────────────────────────────────────────
  //  Choose Media Filter/Sort Events
  // ──────────────────────────────────────────────

  chooseMediaFilterChanged(params: { filterType: string; value: any }) {
    this._track('Choose Media Filter Changed', params);
  }

  chooseMediaSortApplied(params: { sortBy: string }) {
    this._track('Choose Media Sort Applied', params);
  }

  chooseMediaFiltersReset() {
    this._track('Choose Media Filters Reset');
  }

  fileUploadAllDuplicates(params: { fileCount: number }) {
    this._track('File Upload All Duplicates', params);
  }

  // ---------------------------------------------------------------- Story Atlas

  atlasOpened(params: { libraryId: string; mediaCount: number; seasonCount: number }) {
    this._track('Atlas Opened', params);
  }

  atlasEpisodeOpened(params: {
    libraryId: string;
    mediaId: string;
    season?: number;
    episode?: number;
    viaThreadChip?: boolean;
  }) {
    this._track('Atlas Episode Opened', params);
  }

  atlasThreadSelected(params: { libraryId: string; mediaId: string; threadId: number }) {
    this._track('Atlas Thread Selected', params);
  }

  atlasSceneSelected(params: { libraryId: string; mediaId: string; sceneId: number }) {
    this._track('Atlas Scene Selected', params);
  }

  atlasCharacterSelected(params: { libraryId: string; mediaId: string; speakerId: string }) {
    this._track('Atlas Character Selected', params);
  }

  atlasShotsOpened(params: {
    libraryId: string;
    mediaId: string;
    sceneId: number;
    viaCharacterLine?: boolean;
  }) {
    this._track('Atlas Shots Opened', params);
  }

  atlasScenePlayed(params: { libraryId: string; mediaId: string; sceneId: number }) {
    this._track('Atlas Scene Played', params);
  }

  atlasShotPlayed(params: {
    libraryId: string;
    mediaId: string;
    sceneId: number;
    timestamp: number;
  }) {
    this._track('Atlas Shot Played', params);
  }

  /**
   * A `SubtreeErrorBoundary` caught a crash and replaced part of the page.
   *
   * Deliberately separate from Sentry, which answers "what broke". This answers "how
   * often do users hit a broken section, and in which one" — `boundary` is the semantic
   * subtree name (`'LibraryContent'`, `'MomentModal'`), not a component path, so
   * renaming a component doesn't break the dashboard.
   *
   * `afterRetry` is what makes the in-place retry measurable: fired `true` when the
   * subtree threw again after the user pressed "Try again". A retry that does NOT
   * produce a second event is one that worked, so
   * `Section Retry Clicked` minus `afterRetry: true` is the success count.
   */
  sectionRenderFailed(params: { boundary: string; afterRetry: boolean }) {
    this._track('Section Render Failed', params);
  }

  /** The user pressed "Try again" on a `SubtreeErrorBoundary` fallback. */
  sectionRetryClicked(params: { boundary: string }) {
    this._track('Section Retry Clicked', params);
  }
}

export const trackingStore = new TrackingStore();
